import { useState } from 'react';

import type { TrendingBook } from '@/features/library/api/library.api.js';
import { BookModal } from '@/features/library/components/BookModal.js';
import { pickColors } from '@/features/library/components/bookcase/spine-utils.js';
import { useMyShelf } from '@/features/library/hooks/useMyShelf.js';
import { useTrendingBooks } from '@/features/library/hooks/useTrendingBooks.js';
import { SafeCoverImg } from '@/shared/components/SafeCoverImg.js';

/**
 * Tendances BookTok — top 10 sur la page Découvrir.
 * Cartes cliquables → BookModal standard (mêmes actions wishlist / status).
 */
export function DiscoverTrendingRow(): JSX.Element {
  const { data, isLoading } = useTrendingBooks(10);
  const { data: shelfData } = useMyShelf();
  const [selectedBookId, setSelectedBookId] = useState<string | null>(null);

  const selectedBook =
    data?.items.find((b) => b.id === selectedBookId) ?? null;
  const selectedEntry =
    shelfData?.items.find((e) => e.book.id === selectedBookId) ?? null;

  return (
    <section className="flex flex-col gap-3">
      <div>
        <h2 className="font-serif text-2xl font-semibold text-brown">
          Tendances BookTok 🔥
        </h2>
        <p className="font-script text-lg text-brown-soft">
          le top 10 qui fait vibrer la communauté
        </p>
      </div>

      {isLoading && <p className="text-sm text-brown-soft">Chargement…</p>}

      {!isLoading && (!data || data.items.length === 0) && (
        <p className="text-sm text-brown-soft">
          Pas encore de tendances disponibles.
        </p>
      )}

      {data && data.items.length > 0 && (
        <div className="trending-row">
          {data.items.map((book, idx) => (
            <DiscoverTrendingCard
              key={book.id}
              book={book}
              rank={idx + 1}
              onClick={() => setSelectedBookId(book.id)}
            />
          ))}
        </div>
      )}

      {selectedBook && (
        <BookModal
          book={selectedBook}
          entry={selectedEntry}
          onClose={() => setSelectedBookId(null)}
        />
      )}
    </section>
  );
}

function badgeForBook(book: TrendingBook): {
  icon: string;
  label: string;
  title: string;
} {
  const nyt = book.sources.find((s) => s.source === 'nyt-bestsellers');
  if (nyt) {
    return { icon: '📰', label: 'NYT', title: nyt.context ?? 'NYT Bestsellers' };
  }
  if (book.sources.length > 0) {
    const first = book.sources[0]!;
    return {
      icon: '🌐',
      label: first.source,
      title: first.context ?? first.source,
    };
  }
  return { icon: '🔥', label: 'Communauté', title: 'Tendance communauté' };
}

function DiscoverTrendingCard({
  book,
  rank,
  onClick,
}: {
  book: TrendingBook;
  rank: number;
  onClick: () => void;
}): JSX.Element {
  const [c1, c2] = pickColors(book);
  const badge = badgeForBook(book);
  return (
    <article
      className="trending-card cursor-pointer transition-transform hover:-translate-y-0.5"
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      aria-label={`Ouvrir ${book.title}`}
    >
      <div
        className="trending-cover"
        style={
          {
            ['--c1' as string]: c1,
            ['--c2' as string]: c2,
          } as React.CSSProperties
        }
      >
        <span className="rank">#{rank}</span>
        <SafeCoverImg
          url={book.coverUrl}
          className="absolute inset-0 h-full w-full object-cover"
          fallback={<span>{book.title}</span>}
        />
      </div>
      <span className="trending-badge" title={badge.title}>
        <span aria-hidden>{badge.icon}</span>
        {badge.label}
      </span>
      <p className="line-clamp-2 font-serif text-sm font-semibold leading-tight text-brown">
        {book.title}
      </p>
      <p className="truncate text-[11px] text-brown-light">{book.author}</p>
    </article>
  );
}
